import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  CatchUpModeClient,
  CatchUpModeSource,
  InsertCatchUpModeEventIdsMapRequest,
} from 'src/WebApiController';

export const useCatchUpModeEventIdsMap = (
  mapId?: string,
  source?: CatchUpModeSource,
  disabled?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();

  const { trackError, showErrorDialog } = useClientLogContext();

  const queryClient = useQueryClient();
  const catchUpModeClient = new CatchUpModeClient(activeAccountWebClientConfig);

  const shouldQuery = useMemo(
    () =>
      Boolean(
        activeAccountWebClientConfig.activeAccountId && !!mapId && !disabled
      ),
    [activeAccountWebClientConfig.activeAccountId, mapId, disabled]
  );

  const catchUpEventIdsQuery = useQuery({
    queryKey: [
      'CatchUpModeClient.getCatchUpModeEventIdsMapById',
      mapId,
      source,
      disabled,
    ],
    queryFn: async () => {
      if (!shouldQuery) {
        return null;
      }

      const response =
        await catchUpModeClient.getCatchUpModeEventIdsMapById(mapId);

      // Cached event ids is not from the same source
      if (response.catchUpModeSource !== source) {
        return null;
      }

      return response;
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        trackError('CatchUpModeClient.getCatchUpModeEventIdsMapById', error, {
          mapId,
        });
      },
    },
  });

  const mutation = useMutation({
    mutationFn: async ({
      request,
    }: {
      request: InsertCatchUpModeEventIdsMapRequest;
    }) => {
      return await catchUpModeClient.insertCatchUpModeEventIdsMap(request);
    },
    onError: (err: ErrorTypes, { request }, context) => {
      showErrorDialog('CatchUpModeClient.insertCatchUpModeEventIdsMap', err, {
        trackErrorData: { request },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries();
    },
  });

  const onSaveCatchUpModeEventIds = useCallback(
    async (
      catchUpModeSource: CatchUpModeSource,
      eventIds: string[]
    ): Promise<string | null> => {
      const request: InsertCatchUpModeEventIdsMapRequest = {
        catchUpModeSource,
        eventIds,
      };
      const cacheId = await mutation.mutateAsync({ request });
      return cacheId;
    },
    [mutation]
  );

  return {
    keyValueCache: catchUpEventIdsQuery.data,
    onSaveCatchUpModeEventIds,
    isLoading: mutation.isPending,
    isCompleted: mutation.isSuccess,
  };
};
